import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Mail, Plus, RotateCw, Trash2, Users, X } from "lucide-react";
import { authFetch } from "../../lib/authFetch";
import { useClientAuth } from "../../lib/auth-context";

interface CompanyUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: "admin" | "contact" | string;
  status: string;
  activationPending?: boolean;
  roleSociete: string | null;
  lastLoginAt: string | null;
  welcomeEmailSentAt: string | null;
}

const fieldClass = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary/50";
const initialInvite = { firstName: "", lastName: "", email: "", role: "contact", roleSociete: "" };

export default function CompanyUsersSection({ isAdmin }: { isAdmin: boolean }) {
  const { user, logout } = useClientAuth();
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [invite, setInvite] = useState(initialInvite);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch("/api/espace-client/my/users");
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Impossible de charger les contacts.");
      setUsers(data);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible de charger les contacts.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const inviteUser = async (event: React.FormEvent) => {
    event.preventDefault();
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await authFetch("/api/espace-client/my/users/invite", { method: "POST", body: JSON.stringify(invite) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Envoi de l’invitation impossible.");
      setInvite(initialInvite);
      setFormOpen(false);
      setSuccess("Invitation envoyée.");
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Envoi de l’invitation impossible.");
    } finally { setSending(false); }
  };

  const runUserAction = async (id: string, action: () => Promise<Response>, successMessage?: string, revokesSelf = false) => {
    setBusyId(id);
    setError(null);
    setSuccess(null);
    try {
      const response = await action();
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "La modification du contact a échoué.");
      if (revokesSelf && id === user?.id) {
        logout();
        return;
      }
      if (successMessage) setSuccess(successMessage);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "La modification du contact a échoué.");
    } finally { setBusyId(null); }
  };

  const updateRole = (contact: CompanyUser, role: string) => runUserAction(
    contact.id,
    () => authFetch(`/api/espace-client/my/users/${contact.id}/role`, { method: "PATCH", body: JSON.stringify({ role }) }),
    "Rôle mis à jour.",
    contact.role === "admin" && role === "contact",
  );
  const updateStatus = (contact: CompanyUser) => {
    const status = contact.status.toLowerCase() === "active" ? "inactive" : "active";
    return runUserAction(contact.id, () => authFetch(`/api/espace-client/my/users/${contact.id}/status`, {
      method: "PATCH", body: JSON.stringify({ status }),
    }), "Statut mis à jour.", status === "inactive");
  };
  const resend = (contact: CompanyUser) => runUserAction(
    contact.id,
    () => authFetch(`/api/espace-client/my/users/${contact.id}/invitation`, { method: "POST" }),
    "Invitation renvoyée.",
  );
  const remove = (contact: CompanyUser) => {
    if (!window.confirm(`Retirer l’accès de ${contact.email} ?`)) return;
    return runUserAction(contact.id, () => authFetch(`/api/espace-client/my/users/${contact.id}`, { method: "DELETE" }), "Accès retiré.", true);
  };

  return (
    <section className="bg-card border border-border/60 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-border/50">
        <div className="flex items-center gap-2.5"><Users className="w-4 h-4 text-muted-foreground" /><h2 className="text-sm font-semibold">Contacts de la société</h2><span className="text-xs text-muted-foreground">({users.length})</span></div>
        {isAdmin && !formOpen && <button type="button" onClick={() => { setFormOpen(true); setError(null); }} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"><Plus className="w-3.5 h-3.5" />Inviter un contact</button>}
      </div>
      {error && <div className="mx-5 mt-4 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert">{error}</div>}
      {success && <div className="mx-5 mt-4 flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-300" role="status"><Check className="w-4 h-4" />{success}</div>}
      {formOpen && isAdmin && <form onSubmit={inviteUser} className="m-5 rounded-xl border border-border bg-background p-4 space-y-4">
        <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">Inviter un contact</h3><button type="button" onClick={() => { setFormOpen(false); setError(null); }} aria-label="Fermer le formulaire" className="rounded p-1 text-muted-foreground hover:bg-secondary"><X className="w-4 h-4" /></button></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InviteField label="Prénom" value={invite.firstName} onChange={value => setInvite(current => ({ ...current, firstName: value }))} required />
          <InviteField label="Nom" value={invite.lastName} onChange={value => setInvite(current => ({ ...current, lastName: value }))} required />
          <InviteField label="Adresse e-mail" type="email" value={invite.email} onChange={value => setInvite(current => ({ ...current, email: value }))} required />
          <InviteField label="Fonction dans la société" value={invite.roleSociete} onChange={value => setInvite(current => ({ ...current, roleSociete: value }))} />
          <label className="block text-xs font-medium text-muted-foreground">Accès
            <select value={invite.role} onChange={event => setInvite(current => ({ ...current, role: event.target.value }))} className={`${fieldClass} mt-1.5`}>
              <option value="contact">Contact</option><option value="admin">Administrateur</option>
            </select>
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setFormOpen(false)} className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary">Annuler</button>
          <button type="submit" disabled={sending || !invite.firstName.trim() || !invite.lastName.trim() || !invite.email.trim()} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60">{sending && <Loader2 className="w-4 h-4 animate-spin" />}{sending ? "Envoi…" : "Envoyer l’invitation"}</button>
        </div>
      </form>}
      <div className="p-5">
        {loading ? <div className="flex justify-center py-7" role="status"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> :
          users.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Aucun contact associé à cette société.</p> :
            <div className="divide-y divide-border">
              {users.map(contact => {
                const active = contact.status.toLowerCase() === "active";
                const totalAdmins = users.filter(item => item.role === "admin" && item.status === "active").length;
                const usableAdmins = users.filter(item => item.role === "admin" && item.status === "active" && !item.activationPending).length;
                const lastAdmin = contact.role === "admin" && active &&
                  (totalAdmins <= 1 || (!contact.activationPending && usableAdmins <= 1));
                const lastAdminReason = "Dernier administrateur actif — désignez ou réactivez un autre administrateur avant de le désactiver.";
                const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.email;
                return <article key={contact.id} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 lg:flex-row lg:items-center">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{(contact.firstName?.[0] || contact.email[0] || "?").toUpperCase()}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-medium">{name}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${contact.role === "admin" ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"}`}>{contact.role === "admin" ? "Admin" : "Contact"}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${active ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" : "bg-secondary text-muted-foreground"}`}>{contact.status || "En attente"}</span>
                      </div>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" />{contact.email}</p>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {contact.roleSociete && <span>{contact.roleSociete}</span>}
                        <span>Dernière connexion : {formatDate(contact.lastLoginAt)}</span>
                        <span>Invitation : {formatDate(contact.welcomeEmailSentAt)}</span>
                      </div>
                    </div>
                  </div>
                  {isAdmin && <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                    <label className="sr-only" htmlFor={`role-${contact.id}`}>Rôle de {name}</label>
                    <select id={`role-${contact.id}`} value={contact.role} disabled={busyId === contact.id || lastAdmin} onChange={event => updateRole(contact, event.target.value)} className="rounded-md border border-border bg-background px-2 py-1.5 text-xs focus-visible:ring-2 focus-visible:ring-primary">
                      <option value="contact">Contact</option><option value="admin">Admin</option>
                    </select>
                    <button type="button" disabled={busyId === contact.id || lastAdmin} onClick={() => updateStatus(contact)} className="rounded-md border border-border px-2 py-1.5 text-xs hover:bg-secondary disabled:opacity-60">{active ? "Désactiver" : "Activer"}</button>
                    <button type="button" disabled={busyId === contact.id} onClick={() => resend(contact)} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs hover:bg-secondary disabled:opacity-60"><RotateCw className="h-3 w-3" />Renvoyer l’invitation</button>
                    <button type="button" disabled={busyId === contact.id || lastAdmin} onClick={() => remove(contact)} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-60"><Trash2 className="h-3 w-3" />Retirer</button>
                    {busyId === contact.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Mise à jour" />}
                    {lastAdmin && <p className="w-full text-xs text-amber-700 dark:text-amber-400">{lastAdminReason}</p>}
                  </div>}
                </article>;
              })}
            </div>}
      </div>
    </section>
  );
}

function InviteField({ label, value, onChange, type = "text", required = false }: {
  label: string; value: string; onChange: (value: string) => void; type?: string; required?: boolean;
}) {
  return <label className="block text-xs font-medium text-muted-foreground">{label}<input className={`${fieldClass} mt-1.5`} type={type} value={value} required={required} onChange={event => onChange(event.target.value)} /></label>;
}

function formatDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("fr-FR");
}